import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { InfoCard } from "@/components/LegalPage";
import {
  Sparkles, Target, ShieldCheck, Coins, Users, Rocket, Handshake, MessageCircle,
} from "lucide-react";

export default function About() {
  const sections = [
    {
      id: "story", title: "Our Story", icon: Sparkles,
      body: (
        <>
          <p>
            MystiCast is a streaming platform where creators aren&apos;t background music — they&apos;re the game.
            Cameras live, cards dealt, chat blazing. We built the arcade we wanted to hang in.
          </p>
        </>
      ),
    },
    {
      id: "mission", title: "Our Mission", icon: Target, highlight: "volt",
      body: (
        <>
          <p>
            Modern live-streaming has a problem: viewers watch, creators talk, and there&apos;s a screen between them. We&apos;re rebuilding
            the room so the stream <strong>is</strong> the party. Every channel comes with a built-in party-game deck — chess,
            checkers, Drop Four, Orb Drop, Neon Cycles, Paddle Clash, and more free-to-play titles — so viewers can jump from the couch
            to the seat next to you in one click. All games are 100% free with no wagers, no entry fees, and no cash prizes.
          </p>
          <p>
            We want to be the platform where a new creator earns their first tip the day they go live, where casual viewers become
            the reason a stream feels alive, and where nobody has to gamble a paycheck to hang out on a Friday night.
          </p>
        </>
      ),
    },
    {
      id: "principles", title: "The Rules of the House", icon: ShieldCheck, highlight: "pink",
      body: (
        <>
          <InfoCard icon={Coins} title="💰 Streamer-first economics" tone="orange">
            <p><strong>100%</strong> of every tip goes to the verified creator. Not 90%. Not &quot;after fees&quot;. One hundred.</p>
          </InfoCard>
          <InfoCard icon={Sparkles} title="Closed-loop credits, zero gambling" tone="cyan">
            <p>Sparks are free — earned by watching and playing. Gold Coins are a non-refundable, non-transferable, non-withdrawable prepaid platform credit that only flows one direction: viewer → verified creator as a tip. Neither is a currency; neither can leave the platform.</p>
          </InfoCard>
          <InfoCard icon={ShieldCheck} title="🛡 18+ verified" tone="pink">
            <p>Age-gated on entry. Stripe Identity KYC for anyone accepting tips or going live. Safer for creators, cleaner for everyone.</p>
          </InfoCard>
          <InfoCard icon={Users} title="👥 No pay-to-win games">
            <p>Every game on the deck is free-to-play. Sparks earned from playing are for the shop — never for slot pulls or wagers.</p>
          </InfoCard>
        </>
      ),
    },
    {
      id: "future", title: "What We're Building Toward", icon: Rocket,
      body: (
        <>
          <p>
            A creator economy that rewards showing up, playing well, and building a room worth returning to.
            A platform where friendships form over Connect Four at 2 a.m., where LFG posts become recurring crews,
            where community XP actually means something.
          </p>
          <p>
            We&apos;re small, we ship fast, and we&apos;re allergic to enshittification. If we get this right, it&apos;s the place
            you&apos;ll tell a friend about — the kind of platform that grows because it deserves to.
          </p>
        </>
      ),
    },
    {
      id: "say-hi", title: "Say Hi", icon: Handshake,
      body: (
        <p>
          Ideas, complaints, feature requests, love letters — all welcome. Reach out via <Link to="/contact">the contact page</Link>.
          If you&apos;re a creator wanting early Founder status, hit the <Link to="/signup">signup</Link> and verify —
          Founder badges are permanent.
        </p>
      ),
    },
    {
      id: "contact", title: "Contact", icon: MessageCircle,
      body: (
        <p>Reach the team via the <Link to="/contact">contact page</Link>, or email <a href="mailto:support@mysticast.tv">support@mysticast.tv</a>.</p>
      ),
    },
  ];

  return (
    <LegalPage
      icon={Sparkles}
      label="ABOUT US"
      title="We built the arcade we wanted to hang in."
      intro="MystiCast is a streaming platform where creators aren't background music — they're the game. Cameras live, cards dealt, chat blazing."
      updated="February 2026"
      sections={sections}
      contact={{
        title: "Curious about MystiCast?",
        body: "Have a question about the platform, our mission, or how creators earn here? We're happy to chat.",
        cta: { label: "Get in Touch", to: "/contact" },
      }}
    />
  );
}
